/**
 * 📚 AgendaDownloadSection
 * 
 * Componente para descargar la Agenda Numerológica 2026
 * desde dentro de la aplicación (usuarios autenticados).
 * 
 * Usa el endpoint /api/pdf/agenda-auth que valida el JWT
 * y registra la descarga con el email del usuario.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, CheckCircle, AlertCircle, Loader2, BookOpen, Calendar, Sparkles } from 'lucide-react';

// Obtener token de autenticación
function getAuthToken() {
  return localStorage.getItem('auth_token');
}

export default function AgendaDownloadSection() {
  const [downloadState, setDownloadState] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleDownload = async () => {
    setDownloadState('loading');
    setErrorMessage('');

    try {
      const token = getAuthToken();
      
      if (!token) {
        throw new Error('No estás autenticado. Por favor, inicia sesión nuevamente.');
      }

      const response = await fetch('/api/pdf/agenda-auth', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Error al obtener la descarga');
      }

      // Abrir URL de descarga en nueva pestaña
      setDownloadState('success');
      
      // Pequeño delay para mostrar el estado de éxito
      setTimeout(() => {
        window.open(data.downloadUrl, '_blank');
      }, 500);

      // Resetear estado después de un momento
      setTimeout(() => {
        setDownloadState('idle');
      }, 3000);

    } catch (error) {
      console.error('Error descargando agenda:', error);
      setErrorMessage(error.message || 'Error inesperado');
      setDownloadState('error');
      
      // Resetear después de un momento
      setTimeout(() => {
        setDownloadState('idle');
        setErrorMessage('');
      }, 5000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border overflow-hidden"
      style={{ borderColor: '#d4af37' }}
    >
      {/* Header con gradiente */}
      <div 
        className="px-6 py-8 text-white text-center"
        style={{ 
          background: 'var(--color-morado)'
        }}
      >
        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        >
          <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-90" />
        </motion.div>
        <h2 className="text-2xl md:text-3xl font-bold mb-2">
          Agenda Numerológica 2026
        </h2>
        <p className="text-white/80 text-sm md:text-base">
          Tu guía personal para navegar el año con la sabiduría de los números
        </p>
      </div>

      {/* Contenido */}
      <div className="p-6 md:p-8">
        {/* Características */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="flex items-start gap-3 p-4 bg-morado/5 rounded-xl">
            <Calendar className="w-6 h-6 text-morado flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-morado text-sm">Planificación Anual</h3>
              <p className="text-xs text-morado/80 mt-1">
                Calendario completo con energías de cada día
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl">
            <Sparkles className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900 text-sm">Días Especiales</h3>
              <p className="text-xs text-amber-700/80 mt-1">
                Identifica tus momentos de mayor poder personal
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-rose-50 rounded-xl">
            <BookOpen className="w-6 h-6 text-rose-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-rose-900 text-sm">Guía Práctica</h3>
              <p className="text-xs text-rose-700/80 mt-1">
                Interpretaciones y consejos para cada ciclo
              </p>
            </div>
          </div>
        </div>

        {/* Botón de descarga */}
        <div className="flex flex-col items-center">
          <motion.button
            onClick={handleDownload}
            disabled={downloadState === 'loading'}
            whileHover={{ scale: downloadState === 'idle' ? 1.02 : 1 }}
            whileTap={{ scale: downloadState === 'idle' ? 0.98 : 1 }}
            className={`
              relative flex items-center justify-center gap-3 
              px-8 py-4 rounded-xl font-semibold text-lg
              transition-all duration-300 shadow-lg
              ${downloadState === 'idle' 
                ? 'bg-morado hover:opacity-90 text-white hover:shadow-xl' 
                : downloadState === 'loading'
                ? 'bg-morado/50 text-white cursor-wait'
                : downloadState === 'success'
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
              }
            `}
          >
            {downloadState === 'idle' && (
              <>
                <Download className="w-5 h-5" />
                <span>Descargar Agenda PDF</span>
              </>
            )}
            {downloadState === 'loading' && (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Preparando descarga...</span>
              </>
            )}
            {downloadState === 'success' && (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>¡Descarga iniciada!</span>
              </>
            )}
            {downloadState === 'error' && (
              <>
                <AlertCircle className="w-5 h-5" />
                <span>Error en descarga</span>
              </>
            )}
          </motion.button>

          {/* Mensaje de error */}
          {errorMessage && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 text-sm text-red-600 text-center"
            >
              {errorMessage}
            </motion.p>
          )}

          {/* Nota */}
          <p className="mt-4 text-xs text-gray-500 text-center max-w-md">
            La descarga se registrará con tu email de cuenta para llevar un control.
            El PDF se abrirá en una nueva pestaña.
          </p>
        </div>
      </div>

      {/* Footer decorativo */}
      <div 
        className="h-2"
        style={{ 
          background: 'var(--color-morado)'
        }}
      />
    </motion.div>
  );
}
